import type { WiadomoscTreningu } from "@shared/treningTypy.js";
import { Avatar } from "../ui/Avatar.js";

/**
 * Rozmowa z symulowanym rozmowca wg gotowca design-system/snippets/chat.html (CV-2, CV-5), z klasami z przedrostkiem
 * `trening-`: klasy `.msg` i `.bubble` naleza juz do transkryptu analizy (session.css).
 *
 * Odstepstwa od gotowca (zatwierdzona makieta widoku uzytkownika, scratch/makieta-uzytkownik/d5.png):
 * - obie strony maja dymek na `--surface-sunken`, wypowiedz uzytkownika ma dodatkowo obrys i stoi po prawej: gotowiec daje
 *   uzytkownikowi dymek `--primary`, a w makiecie rozmowa jest spokojna i bez czarnych plam;
 * - strony rozroznia tez NAPIS nad dymkiem ("Rozmówca" / "Ty"), nie tylko polozenie i obrys (A-7);
 * - wskaznik "Rozmówca pisze..." ma zawsze widoczny tekst, nie tylko kropki.
 *
 * Aktywna rozmowa to region `role="log"` z `aria-live="polite"`: nowe wiadomosci czytnik oglasza bez przerywania, a fokusu
 * nie rusza. W trybie tylko do odczytu (zakonczony trening) nic sie nie zmienia, wiec jest zwykla lista bez regionu. Lista
 * zostaje lista (ol/li) takze wewnatrz regionu: rola `log` nie moze stac na samym `ol`, bo odebralaby `li` ich rodzica.
 */

export const NAZWA_ROZMOWCY = "Rozmówca";
export const NAZWA_UZYTKOWNIKA = "Ty";
const ETYKIETA_ROZMOWY = "Rozmowa z rozmówcą";

export function Rozmowa({
  wiadomosci,
  rozmowcaPisze = false,
  aktywna = true,
}: {
  wiadomosci: readonly WiadomoscTreningu[];
  /** Pokazuje wskaznik "Rozmówca pisze..." na koncu rozmowy. */
  rozmowcaPisze?: boolean;
  /** False: rozmowa tylko do odczytu (zakonczony trening), bez regionu oglaszanego czytnikowi. */
  aktywna?: boolean;
}): React.JSX.Element {
  const lista = (
    <ol className="trening-rozmowa" aria-label={aktywna ? undefined : ETYKIETA_ROZMOWY}>
      {wiadomosci.map((wiadomosc, i) => (
        <li key={`${i}-${wiadomosc.utworzono}`} className="trening-wiadomosc" data-rola={wiadomosc.rola}>
          <div className="trening-wiadomosc__naglowek">
            {wiadomosc.rola === "klient" && (
              <span className="trening-wiadomosc__awatar">
                <Avatar nazwa={NAZWA_ROZMOWCY} rozmiar="s" />
              </span>
            )}
            <span className="trening-wiadomosc__nazwa">
              {wiadomosc.rola === "klient" ? NAZWA_ROZMOWCY : NAZWA_UZYTKOWNIKA}
            </span>
          </div>
          <div className="trening-dymek">{wiadomosc.tekst}</div>
        </li>
      ))}
      {rozmowcaPisze && (
        <li className="trening-wiadomosc" data-rola="klient">
          <div className="trening-wiadomosc__naglowek">
            <span className="trening-wiadomosc__awatar">
              <Avatar nazwa={NAZWA_ROZMOWCY} rozmiar="s" />
            </span>
            <span className="trening-wiadomosc__nazwa">{NAZWA_ROZMOWCY}</span>
          </div>
          <div className="trening-dymek trening-pisze">
            <span className="trening-pisze__kropki" aria-hidden="true">
              <span className="trening-pisze__kropka" />
              <span className="trening-pisze__kropka" />
              <span className="trening-pisze__kropka" />
            </span>
            <span>Rozmówca pisze...</span>
          </div>
        </li>
      )}
    </ol>
  );

  if (!aktywna) return lista;
  return (
    <div role="log" aria-label={ETYKIETA_ROZMOWY} aria-live="polite" aria-relevant="additions">
      {lista}
    </div>
  );
}
